import {AfterViewInit, Component, inject, input, InputSignal, signal, WritableSignal} from '@angular/core';
import {ObjectCopyService} from "../../../object-copy/object-copy.service";
import {SectionsContainer} from "../../../core/definitions/sections-container";
import {SharedModule} from "../../../shared/shared.module";
import {MetaField} from "../../../core/definitions/meta-field";
import {MatButton} from "@angular/material/button";
import {MatDialogRef} from "@angular/material/dialog";
import {SectionViewCopy} from "../../../object-copy/section-view-copy/section-view-copy";
import {EditObjectDialogComponent} from "../../edit-object-dialog/edit-object-dialog.component";
import {ObjectStorageService} from "../../../core/object-storage.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {CmsApiService} from "../../../core/cms-api.service";
import {ModelSchema} from "../../../core/definitions/model-schema";
import {Setting} from "../../../core/definitions/setting";

@Component({
  selector: 'app-consists-of-parent-copy',
  imports: [
    SharedModule,
    MatButton,
    MatProgressSpinner,
    SectionViewCopy
  ],
  templateUrl: './consists-of-parent-copy.html',
  styleUrl: './consists-of-parent-copy.scss',
})
export class ConsistsOfParentCopy implements AfterViewInit {
  private readonly cmsApi: CmsApiService = inject(CmsApiService);
  private readonly objectCopyService: ObjectCopyService = inject(ObjectCopyService);
  private readonly objectStoreService: ObjectStorageService = inject(ObjectStorageService);

  dialogRef: InputSignal<MatDialogRef<EditObjectDialogComponent>> = input();
  sectionsContainer: InputSignal<SectionsContainer> = input();
  sourceArtifactId: InputSignal<string> = input();
  targetCount: InputSignal<number> = input(1);

  clientSettings: WritableSignal<Setting[]> = signal([]);
  loading: WritableSignal<boolean> = signal(false);
  modelSchema: WritableSignal<ModelSchema> = signal(null);

  ngAfterViewInit() {
    this.cmsApi.getModelSchema({
      modelName: this.sectionsContainer().rootObject.object_type
    }).then((schema) => {
      this.modelSchema.set(schema);
      return this.cmsApi.getSettings();
    }).then((settings) => {
      this.clientSettings.set(settings);
    });

    for (const section of this.sectionsContainer().sections) {
      if (section.fields?.length) {
        for (const field of section.fields) {
          if (field.is_required === 'is_required' || field.key === 'identification_number') {
            this.objectCopyService.toggleField(true, field);
          }
        }
      }
    }
  }

  get anyFieldIsSelected(): boolean {
    return !!this.objectCopyService.selectedFields().length;
  }

  cancel(): void {
    this.dialogRef().close(false);
  }

  async createAndConnectObject() {
    this.loading.set(true);

    const targetObjects = [];
    for (let i = 0; i < this.targetCount(); i++) {
      const targetObject = this.objectCopyService.createSuperObjectModel(
        this.sectionsContainer(),
        this.sourceArtifactId(),
        this.modelSchema(),
        this.clientSettings()
      );
      targetObjects.push(this.objectStoreService.getCleanObject(targetObject));
    }

    const storeResult = await this.objectStoreService.storeObjects(targetObjects);
    const newObjects = await this.objectStoreService.loadObjects(storeResult.artifact_ids);

    this.loading.set(false);

    // Created copies passed to the callback defined in `EditFieldSearchSelectorHandler.openCreateNew`,
    // where they are finally added as child items to the parent object.
    this.dialogRef().close(newObjects);
  }

  getFieldIsRequired(field: MetaField): boolean {
    return field.is_required === 'is_required' || field.key === 'identification_number';
  }
}
